import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { AlertCircle, CheckCircle2, FileText } from "lucide-react";

import { WORD_ADDIN_PRICING } from "@/lib/content";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { submitForm, SubmitFormError } from "@/lib/submit-form";
import { track } from "@/lib/analytics";

const waitlistSchema = z.object({
  email: z.string().email("A valid email address is required"),
});

type WaitlistFormValues = z.infer<typeof waitlistSchema>;

const dlg = WORD_ADDIN_PRICING.waitlistDialog;

interface WordAddinWaitlistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Stable tier identifier ("Free" / "Pro") submitted alongside the email
   * and sent in the analytics payload. Don't rename — historical data is
   * keyed off these values.
   */
  selectedTier: string;
  /**
   * Friendly label rendered in the dialog title (e.g. "Solo Counsel",
   * "Team Pro"). Falls back to `selectedTier` so older callers keep
   * working. Renaming this label does NOT change what gets sent to the
   * backend; that stays `selectedTier`.
   */
  selectedTierLabel?: string;
  /**
   * Where the click came from (e.g. "pricing-page", "word-addin-page-hero").
   * Threaded through the analytics event so we can see which surface
   * drives signups without changing the form submission contract.
   */
  source?: string;
}

export function WordAddinWaitlistDialog({
  open,
  onOpenChange,
  selectedTier,
  selectedTierLabel,
  source,
}: WordAddinWaitlistDialogProps) {
  const tierLabel = selectedTierLabel ?? selectedTier;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<WaitlistFormValues>({
    resolver: zodResolver(waitlistSchema),
    defaultValues: { email: "" },
  });

  async function onSubmit(values: WaitlistFormValues) {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await submitForm("word-addin-waitlist", { ...values, tier: selectedTier });
      track("marketing_form_submitted", {
        source: "word-addin-waitlist",
        tier: selectedTier,
        ...(source ? { surface: source } : {}),
      });
      setIsSuccess(true);
    } catch (err) {
      if (err instanceof SubmitFormError && err.fieldErrors) {
        for (const [field, message] of Object.entries(err.fieldErrors)) {
          form.setError(field as keyof WaitlistFormValues, {
            type: "server",
            message,
          });
        }
      }
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleOpenChange(open: boolean) {
    if (!open) {
      form.reset();
      setIsSuccess(false);
      setSubmitError(null);
    }
    onOpenChange(open);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="h-8 w-8 rounded-lg bg-brand-gradient flex items-center justify-center">
              <FileText className="h-4 w-4 text-white" />
            </div>
            <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
              {dlg.eyebrow}
            </span>
          </div>
          <DialogTitle className="text-xl">
            {dlg.title}
            {tierLabel ? ` — ${tierLabel} plan` : ""}
          </DialogTitle>
          <DialogDescription>{dlg.description}</DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="text-center py-8">
            <div className="h-14 w-14 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mb-4">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h3 className="text-lg font-bold mb-2">{dlg.successTitle}</h3>
            <p className="text-sm text-muted-foreground">{dlg.successBody}</p>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{dlg.emailLabel}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={dlg.emailPlaceholder}
                        type="email"
                        data-testid="word-addin-waitlist-email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {submitError && (
                <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-400">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{submitError}</span>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-brand-gradient text-white border-0 hover:opacity-90"
                disabled={isSubmitting}
                data-testid="word-addin-waitlist-submit"
              >
                {isSubmitting ? dlg.ctaLoading : dlg.ctaLabel}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                {dlg.privacyNote}
              </p>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
